import type {
  TestCompletionEndpointType,
  TestCompletionMode,
} from '@/api/testCompletion'
import type { LearnerLibraryResponse } from '@/types/coaching'

export interface TestCompletionEndpointOption {
  type: TestCompletionEndpointType
  contentId: number
  label: string
  path: string[]
  completed: boolean
}

const TARGET_ENDPOINT_TYPES: TestCompletionEndpointType[] = ['LESSON', 'CHAPTER_END', 'COURSE_END']

export const testCompletionEndpointKey = (option: TestCompletionEndpointOption) =>
  `${option.type}:${option.contentId}`

export const endpointTypeRequiresTarget = (type: TestCompletionEndpointType) =>
  TARGET_ENDPOINT_TYPES.includes(type)

export function buildEndpointOptions(
  library: LearnerLibraryResponse | undefined,
  mode: TestCompletionMode,
): TestCompletionEndpointOption[] {
  const tree = library?.tree ?? []
  const lessons = tree.flatMap((curriculum) =>
    curriculum.courses.flatMap((course) =>
      course.modules.flatMap((module) => module.lessons),
    ),
  )
  const lessonOptions = tree.flatMap((curriculum) =>
    curriculum.courses.flatMap((course) =>
      course.modules.flatMap((module) =>
        module.lessons.map((lesson) => ({
          type: 'LESSON' as const,
          contentId: lesson.lessonId,
          label: lesson.title,
          path: [curriculum.title, course.title, module.title, lesson.title],
          completed: lesson.status === 'completed',
        })),
      ),
    ),
  )
  if (mode === 'LEARNER_ACTIVITY_ONLY') {
    // The backend plans from the first incomplete timeline item to the endpoint and runs learner-only
    // mode for exactly one item, so only the next lesson in timeline order can succeed.
    const nextLesson = lessonOptions.find((option) => !option.completed)
    return nextLesson ? [nextLesson] : []
  }

  const fallbackContentId = lessons[0]?.lessonId ?? tree[0]?.curriculumId
  const relativeOptions: TestCompletionEndpointOption[] = fallbackContentId == null ? [] : [
    {
      type: 'BEFORE_NEXT_CHAPTER_TEST',
      contentId: fallbackContentId,
      label: '',
      path: [],
      completed: false,
    },
    {
      type: 'NEXT_CUMULATIVE_TEST',
      contentId: fallbackContentId,
      label: '',
      path: [],
      completed: false,
    },
  ]
  const chapterOptions = tree.flatMap((curriculum) =>
    curriculum.courses.flatMap((course) =>
      course.modules.map((module) => ({
        type: 'CHAPTER_END' as const,
        contentId: module.moduleId,
        label: module.title,
        path: [curriculum.title, course.title, module.title],
        // Library chapter counts describe lesson completion only. The chapter test may still be pending.
        completed: false,
      })),
    ),
  )
  const courseOptions = tree.flatMap((curriculum) =>
    curriculum.courses.map((course) => ({
      type: 'COURSE_END' as const,
      contentId: course.courseId,
      label: course.title,
      path: [curriculum.title, course.title],
      // A course endpoint can still include pending chapter/cumulative tests after all lessons are done.
      completed: false,
    })),
  )
  return [...relativeOptions, ...lessonOptions, ...chapterOptions, ...courseOptions]
}
